import { IMG } from "./assets";

// Six category landing pages — long-form content focussed on India's edge,
// what makes the category different from the rest of the world, the testing
// protocols Asia Sourcing runs, and the clusters that power each vertical.

export const categories = [
  // ───────────────────────────── LIGHTING ─────────────────────────────
  {
    id: "lighting",
    name: "Lighting",
    tagline: "Brass · glass · ceramic · hand-finished",
    image: IMG.lightingProduct,
    spec: "MOQ 500 · 35-day lead · BSCI / Sedex audited",
    intro:
      "Decorative and architectural lighting — pendants, sconces, lamps, shades and chandeliers — sourced from India's two lighting capitals: Moradabad (brass) and Firozabad (glass). Two cities, four hundred years of craft, one documented chain.",
    whyIndia: [
      "Moradabad alone produces more brassware than any other city on Earth — the entire trade is built around the lighting cluster. No other geography can deliver brass volume + craft + finishing in the same supply chain.",
      "Firozabad's furnaces have been running since the Mughal era; the city produces a remarkable share of the world's hand-blown glass shades and decorative ware.",
      "India is one of the few countries where electroplating, hand-spinning, sand-casting and lacquer-finishing can all happen on the same factory floor — for the same SKU.",
      "Labour fluency in hand-finishing means CNC + hand-spun parts can be combined economically — a hybrid impossible at European cost and impractical at Chinese MOQs.",
    ],
    difference: [
      ["China", "Pressed, die-cast volume. Limited brass craft, no hand-spinning at retail price points."],
      ["Vietnam", "Strong on rattan / natural shades. Brass and glass capacity is limited."],
      ["Turkey", "Beautiful Bosphorus brass — but MOQs and lead times don't compete on volume programs."],
      ["India", "Hand-spun + CNC, electroplated finishes, photometric-tested. Volume + craft in one quote."],
    ],
    testing: [
      ["Photometric (LM-79)", "Light output, colour temperature (CCT), CRI and beam angle measured in-house."],
      ["Electrical safety", "Earth continuity, hi-pot, insulation resistance, leakage current — CE / UKCA / cULus dossiers prepared."],
      ["Salt-spray (ASTM B117)", "48 / 96 / 240-hour cycles for brass, copper, nickel and antique finishes."],
      ["Finish & adhesion", "Cross-hatch tape test (ASTM D3359), gloss meter, pencil hardness."],
      ["Drop / vibration packing", "ISTA-3A on every pendant and shade; photographs archived per carton."],
      ["AQL 2.5", "Critical 0 · Major 2.5 · Minor 4.0 — every container, in-line and final random inspection."],
    ],
    materials: ["Brass", "Copper", "Glass", "Ceramic", "Iron", "Aluminium"],
    finishes: [
      "Polished brass · matte · antique",
      "Black powder-coat · gunmetal · bronze",
      "Hand-blown clear · smoke · lustred",
      "Frosted · cut · lampworked accents",
    ],
    facts: [
      ["MOQ", "500 units"],
      ["Lead time", "35–45 days"],
      ["Clusters", "Moradabad · Firozabad"],
      ["Certifications", "BSCI · Sedex · ISO 9001"],
      ["QC standard", "AQL 2.5 · LM-79 · ASTM B117"],
      ["Heritage", "400+ years"],
    ],
    clusters: ["moradabad", "firozabad"],
    products: [
      "Pendants & chandeliers",
      "Wall sconces",
      "Table & floor lamps",
      "Architectural ware",
      "Outdoor / garden lighting",
      "Hand-blown decorative shades",
    ],
    cta: "Send a brief for a lighting program",
  },

  // ───────────────────────────── FURNITURE ─────────────────────────────
  {
    id: "furniture",
    name: "Furniture",
    tagline: "Solid wood · iron · industrial · hand-carved",
    image: IMG.furnitureProduct,
    spec: "MOQ 50 · 60-day lead · FSC sheesham · moisture-controlled",
    intro:
      "Solid-wood and metal furniture — dining, bedroom, seating, occasional and industrial — sourced from India's two furniture hubs: Jodhpur (solid sheesham, mango, iron) and Saharanpur (hand-carved screens, panels, inlay). FSC chain-of-custody available end-to-end.",
    whyIndia: [
      "Jodhpur is the world's largest exporter of solid-wood furniture by SKU count — kiln-dried sheesham and mango with verified 10–12% moisture content.",
      "Saharanpur's carving tradition is registered with India's GI Registry; intricate hand-carving at scale is uneconomic in any other geography.",
      "Iron forging, brass inlay, bone inlay and mother-of-pearl inlay all sit within a 200-km radius of Jodhpur — multi-material SKUs ship as one container.",
      "Single-shop integration: dining + bedroom + living ranges can be built in one factory, sampled in six weeks, signed off before catalogue.",
    ],
    difference: [
      ["China", "Engineered wood, MDF, veneers at low MOQs. Solid hardwood at retail-friendly cost is rare."],
      ["Vietnam", "Strong rubberwood and engineered. Solid sheesham/mango not available; carving is light."],
      ["Indonesia", "Excellent teak — but limited industrial-iron, smaller carving capacity, longer lead times."],
      ["India", "FSC sheesham + mango + acacia, hand-carved + machine, iron forging in-cluster. 50-unit MOQ."],
    ],
    testing: [
      ["Moisture meter", "Every batch verified at 10–12% moisture content before assembly and before container."],
      ["Joinery & structural", "Mortise-tenon, dovetail, screw-pull and load testing per EN 1728 / BIFMA where required."],
      ["Finish testing", "Pencil hardness, gloss meter, chemical resistance (water, alcohol, household cleaners)."],
      ["Heavy-metal lab", "Lead, cadmium, chromium VI checked on lacquer, stains and powder-coats."],
      ["FSC chain-of-custody", "Full timber provenance documents on FSC orders — mill to invoice."],
      ["AQL 2.5", "Final random inspection on 100% of containers, with carton-level photo evidence."],
    ],
    materials: ["Sheesham (Indian rosewood)", "Mango wood", "Acacia", "Teak", "Wrought iron", "Recycled wood"],
    finishes: [
      "Natural oil · wax · lacquer matte / satin / gloss",
      "Hand-distressed · two-tone · cerused",
      "Powder-coated iron (black, gunmetal, brass)",
      "Brass / bone / mother-of-pearl inlay",
    ],
    facts: [
      ["MOQ", "50 units"],
      ["Lead time", "55–70 days"],
      ["Clusters", "Jodhpur · Saharanpur"],
      ["Certifications", "FSC · BSCI · ISO 9001"],
      ["QC standard", "Moisture · structural · finish · AQL 2.5"],
      ["Heritage", "Princely-era craft + modern industrial"],
    ],
    clusters: ["jodhpur", "saharanpur"],
    products: [
      "Dining tables & chairs",
      "Bedroom & wardrobes",
      "Sofas, ottomans, benches",
      "Industrial-iron tables & consoles",
      "Carved screens & panels",
      "Sideboards & cabinets",
    ],
    cta: "Send a brief for a furniture program",
  },

  // ───────────────────────────── HOME DÉCOR ─────────────────────────────
  {
    id: "home-decor",
    name: "Home décor",
    tagline: "Ceramic · blue pottery · mixed media · gifting",
    image: IMG.pottery,
    spec: "MOQ 300 · 30-45 day lead · breakage-tested ISTA-3A packing",
    intro:
      "Mixed-material home décor — ceramic and blue pottery vases, planters and tableware, mixed-media gifting, seasonal and private-label programs. Jaipur is the GI-tagged heart of blue pottery; Noida and Delhi NCR run multi-material consolidation for retail programs.",
    whyIndia: [
      "Jaipur blue pottery is a GI-protected craft tradition — a hand-painted Mughal-era technique that survives nowhere else in the world.",
      "India is the only country where metal + wood + glass + ceramic SKUs can be combined into one container under a single QC chain.",
      "Hand-painting at retail price points: 4-8 skilled painters per workshop means 18-SKU décor lines ship in nine weeks, not nine months.",
      "Heavy-metal lab testing on every glaze batch — lead, cadmium — for FDA / Prop 65 / EU 1935/2004 compliance.",
    ],
    difference: [
      ["China", "Strong on machine ceramic. Hand-painted décor at retail cost is increasingly rare."],
      ["Vietnam", "Excellent stoneware. Limited mixed-material programs, no blue pottery."],
      ["Portugal", "Beautiful azulejo / faience — premium pricing, not for volume retail."],
      ["India", "Hand-painted blue pottery, terracotta, stoneware + mixed-media gifting. GI-tagged craft."],
    ],
    testing: [
      ["Lead & cadmium leach", "FDA, Prop 65, EU 1935/2004 lab certificates on every glaze batch and colourway."],
      ["Thermal shock", "Pottery cycled hot-cold to verify no crazing on landing."],
      ["Drop test (ISTA-3A)", "Pulp inners, fluted dividers, drop-test certified packing for every breakable SKU."],
      ["Finish & paint adhesion", "Cross-hatch, abrasion and chemical resistance on hand-painted glaze."],
      ["Dimensional & finish", "AQL 2.5 with photo-documented in-line and final inspection."],
      ["Material composition", "Lab-verified bone-china / stoneware / earthenware classification."],
    ],
    materials: ["Blue pottery (quartz body)", "Stoneware", "Terracotta", "Bone china", "Mango wood", "Brass"],
    finishes: [
      "Hand-painted glaze (cobalt, copper, manganese)",
      "Stencil + freehand decoration",
      "Reactive / crystalline glazes",
      "Mixed-media: ceramic + brass + wood",
    ],
    facts: [
      ["MOQ", "300 units"],
      ["Lead time", "30–45 days"],
      ["Clusters", "Jaipur · Noida · Delhi NCR"],
      ["Certifications", "Fair-trade · GI tag · FDA / Prop 65 lab"],
      ["QC standard", "AQL 2.5 · ISTA-3A · lead / cadmium lab"],
      ["Heritage", "Mughal-era · GI-tagged"],
    ],
    clusters: ["jaipur", "noida", "saharanpur"],
    products: [
      "Vases & planters",
      "Decorative bowls & platters",
      "Tableware & serveware",
      "Wall décor & mirrors",
      "Gifting & seasonal programs",
      "Boxes, trays, candles",
    ],
    cta: "Send a brief for a home-décor program",
  },

  // ───────────────────────────── TEXTILE ─────────────────────────────
  {
    id: "textile",
    name: "Textile",
    tagline: "Block print · weave · soft furnishing · recycled yarn",
    image: IMG.textileProduct,
    spec: "MOQ 500 · 40-day lead · GOTS / GRS available",
    intro:
      "Soft furnishing, throws, rugs, cushions and block-printed textiles — sourced from Panipat (the recycled-textile capital of India) and Jaipur (block print, GI-tagged). GRS and GOTS-certified programs run end-to-end with fibre-content and colour-fastness lab testing.",
    whyIndia: [
      "Panipat is the recycled-textile capital of India — the world's largest concentration of GRS-certified recycled-yarn mills.",
      "India is the world's largest producer of cotton and a leading producer of jute — vertical integration from fibre to finish is unmatched.",
      "Block printing (Jaipur, Bagru, Sanganer) is a GI-tagged craft that exists nowhere else at this price point.",
      "Handloom + powerloom + tufted programs sit in the same cluster — colour-matched across techniques in one shipment.",
    ],
    difference: [
      ["China", "Powerloom and synthetic-dominated. Hand block-print and GRS recycled at scale: limited."],
      ["Turkey", "Beautiful Anatolian weaves — premium pricing, smaller capacity."],
      ["Pakistan", "Strong on home textiles — political/logistics risk for EU/US buyers; smaller certified base."],
      ["India", "GRS + GOTS + handloom + block-print, fibre-to-finish vertical integration. 500-unit MOQ."],
    ],
    testing: [
      ["GRS / GOTS chain-of-custody", "Transaction certificates per shipment; fibre-content lab verified."],
      ["Colour-fastness (ISO 105)", "Wash, rub (wet/dry), light and perspiration testing on every colourway."],
      ["Pilling & abrasion", "Martindale / Pilling box tests for upholstery and throws."],
      ["Fibre-content & GSM", "Lab-verified composition and weight per square metre on every roll."],
      ["Dimensional stability", "Pre-wash shrinkage tested and labelled."],
      ["AZO / heavy-metal / formaldehyde", "REACH and Prop 65-compliant lab testing on dyes and finishes."],
    ],
    materials: ["GRS recycled yarn", "GOTS organic cotton", "Wool", "Jute", "Linen", "Viscose"],
    finishes: [
      "Block-print (hand carved wood blocks)",
      "Screen print · digital print",
      "Handloom · powerloom · tufted",
      "Stonewash · acid-wash · enzyme-finished",
    ],
    facts: [
      ["MOQ", "500 units"],
      ["Lead time", "40–60 days"],
      ["Clusters", "Panipat · Jaipur"],
      ["Certifications", "GOTS · GRS · OEKO-TEX"],
      ["QC standard", "ISO 105 · Martindale · fibre lab"],
      ["Heritage", "Block-print: 16th century · Panipat: 1947"],
    ],
    clusters: ["panipat", "jaipur"],
    products: [
      "Throws & blankets",
      "Cushion covers",
      "Rugs (tufted, hand-knotted, flat-weave)",
      "Table linen",
      "Bed linen & quilts",
      "Curtains & soft furnishing",
    ],
    cta: "Send a brief for a textile program",
  },

  // ───────────────────────────── BRASS & METALWARE ─────────────────────────────
  {
    id: "brass-metalware",
    name: "Brass & metalware",
    tagline: "Sand-cast · spun · electroplated · architectural",
    image: IMG.brassLathe,
    spec: "MOQ 500 · 35-day lead · BSCI / Sedex · photometric + salt-spray",
    intro:
      "Brass, copper, aluminium and mixed-metal ware — decorative, architectural and tableware — sourced from Moradabad, India's Brass City for more than four hundred years. Our headquarters has been on these foundry floors since 2002.",
    whyIndia: [
      "Moradabad is the world's brass capital — no other city has the same density of foundries, spinning units, electroplating lines and skilled artisans.",
      "Multi-technique fluency: sand casting, centrifugal casting, gravity die-cast, hand-spinning and CNC spinning all available in one cluster.",
      "Hand-patination and hand-lacquering at retail price points — antique, distressed and aged finishes are an Indian specialty.",
      "Salt-spray, photometric, electrical and finish-testing run on the factory floor by our in-house team.",
    ],
    difference: [
      ["China", "Volume die-cast — strong but limited on hand-finishing and antique patination."],
      ["Turkey", "Beautiful Bosphorus brass — premium, smaller capacity, longer lead times."],
      ["Morocco", "Hand-hammered brass — niche capacity, limited industrial finishing."],
      ["India / Moradabad", "Cast + spun + plated + patinated + photometric-tested. Volume + craft + finish."],
    ],
    testing: [
      ["Salt-spray (ASTM B117)", "48 / 96 / 240-hour cycles for electroplated and lacquered surfaces."],
      ["Photometric (LM-79)", "Light output, CCT, CRI for any brass lighting variants."],
      ["Adhesion & finish", "Cross-hatch (ASTM D3359), pencil hardness, gloss meter."],
      ["Heavy-metal lab", "Lead, cadmium, nickel-release for food-contact and skin-contact items."],
      ["Dimensional & weight", "Caliper, weight per piece, finish thickness measured."],
      ["AQL 2.5 + photographs", "In-line, final and container-loading inspection with photo dossier."],
    ],
    materials: ["Brass (yellow, red, gunmetal)", "Copper", "Aluminium", "Iron + brass composite", "Stainless steel"],
    finishes: [
      "Polished · matte · brushed",
      "Antique · aged · distressed patina",
      "Electroplated nickel · copper · gunmetal · black-chrome",
      "Powder-coat · lacquer (matte, satin, gloss)",
    ],
    facts: [
      ["MOQ", "500 units"],
      ["Lead time", "35–45 days"],
      ["Clusters", "Moradabad · Delhi NCR"],
      ["Certifications", "BSCI · Sedex · ISO 9001"],
      ["QC standard", "AQL 2.5 · ASTM B117 · LM-79"],
      ["Heritage", "400+ years"],
    ],
    clusters: ["moradabad", "noida", "delhi"],
    products: [
      "Decorative ware (vases, bowls, hurricanes)",
      "Architectural metal (railings, panels, hardware)",
      "Bar & barware",
      "Lighting components & fittings",
      "Tableware & flatware",
      "Sculpture & gifting",
    ],
    cta: "Send a brief for a brass / metalware program",
  },

  // ───────────────────────────── FASHION ACCESSORIES ─────────────────────────────
  {
    id: "fashion-accessories",
    name: "Fashion accessories",
    tagline: "Leather · jewellery · embellished apparel · stone-inlay",
    image: IMG.leatherBag,
    spec: "MOQ 100 · 40-day lead · LWG / Polki-Kundan · BSCI audited",
    intro:
      "Sculptural leather handbags, polki & kundan jewellery, stone-inlay mosaic clutches and embroidered apparel — sourced from India's two fashion-accessory capitals: Jaipur (jewellery + stone-inlay) and Delhi NCR (leather + apparel consolidation). One quote, multi-category SKUs, container-load ready.",
    whyIndia: [
      "Jaipur is the world's largest centre for polki, kundan and meenakari jewellery — a Mughal-era technique held in family workshops for 6+ generations.",
      "Stone-inlay (lapis, turquoise, coral, mother-of-pearl on brass) is a uniquely Indian discipline — Jaipur and Udaipur are the only places that do it at retail price points.",
      "Indian leatherwork combines vegetable-tan hide with hand-stitching, brass-thread inlay and lacquer-finishing — finishes that Italy charges 4× for and China simply doesn't offer.",
      "Hand-embroidery (zardosi, aari, chikankari, bagh) at scale: 200+ embroiderers per cluster means a 36-SKU apparel line ships in 60 days, not 6 months.",
    ],
    difference: [
      ["China", "Strong on synthetic leather + machine jewellery. Hand-set polki, kundan and stone-inlay at retail price points: not available."],
      ["Italy / France", "Beautiful leather and couture embroidery — premium pricing, MOQ 1+ per piece, lead times 90–120 days."],
      ["Turkey", "Strong on bazaar metalware and embroidered textiles — limited capacity on leather goods and certified hide."],
      ["India", "Polki + kundan + meenakari + stone-inlay + vegetable-tan leather + zardosi embroidery, in one container. MOQ 100, lead 40–55 days."],
    ],
    testing: [
      ["LWG-certified leather", "Vegetable-tan and chrome-tan hides traced to Leather Working Group gold/silver-rated tanneries; restricted-substance lab tests on every batch."],
      ["Heavy-metal & nickel-release", "EN 1811 nickel release on metal hardware, lead/cadmium on plating and stones — REACH + Prop 65 compliant."],
      ["Hallmarking & stone authentication", "BIS hallmarking on silver jewellery (925), independent lab certificates for polki/kundan stones and pearl grading where applicable."],
      ["Colour-fastness on embroidery", "ISO 105 wash/rub/light tests on embroidered apparel; threadcount and zardosi pull-test per garment."],
      ["Stitch density & strength", "Stitches/inch verified, pull-load test on handles, straps and zip pulls (ISO 12947 for upholstery-grade leather)."],
      ["AQL 1.5 / 2.5", "Critical 0 · Major 1.5 (apparel + jewellery) · 2.5 (bags) — final random + in-line inspection with photo-documented dossier."],
    ],
    materials: ["Vegetable-tan leather", "925 silver", "Brass with polki / kundan settings", "Stone inlay (lapis, turquoise, coral, MOP)", "Silk + viscose for apparel", "Zardosi thread (gold / silver)"],
    finishes: [
      "Hand-stitched · machine-stitched · saddle-stitch",
      "Brass-thread inlay · gold-foil pressed",
      "Antique silver · oxidised · polished · two-tone gold",
      "Zardosi · aari · chikankari · bagh hand embroidery",
    ],
    facts: [
      ["MOQ", "100 units"],
      ["Lead time", "40–55 days"],
      ["Clusters", "Jaipur · Delhi NCR"],
      ["Certifications", "LWG · BIS · BSCI · Sedex"],
      ["QC standard", "AQL 1.5 · EN 1811 · ISO 105"],
      ["Heritage", "Mughal-era · 6+ generations"],
    ],
    clusters: ["jaipur", "delhi", "noida"],
    products: [
      "Sculptural handbags & clutches",
      "Polki / kundan jewellery sets",
      "Stone-inlay mosaic clutches",
      "Silver cuffs, bangles & rings",
      "Embroidered kaftans, robes, abayas",
      "Belts, wallets & small leather goods",
    ],
    cta: "Send a brief for a fashion-accessories program",
  },
];

export const getCategory = (id) => categories.find((c) => c.id === id);
export const categoryIds = categories.map((c) => c.id);
